import {
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
} from "@/lib/help-parties";
import type {
  HelpArticle,
  HelpArticleId,
  HelpCategory,
} from "@/lib/registrant-help/types";

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "Create your account, verify email, and find your way around the portal.",
  },
  {
    id: "registrations",
    title: "Registrations",
    summary: "Register, renew, view, cancel, and de-register properties.",
  },
  {
    id: "payments",
    title: "Payments & billing",
    summary: "Pay Now, Pay Later, invoices, checks, and Stripe billing.",
  },
  {
    id: "organization",
    title: "Organization",
    summary:
      "Account setup, team invites, roles, billing account, portal access, and profile settings.",
  },
  {
    id: "inspections",
    title: "Inspections & properties",
    summary: "Assign inspections and review unregistered properties.",
  },
];

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "A tour of the registrant portal: account setup, dashboard, registrations, payments, and who can do what.",
    category: "getting-started",
    keywords: ["overview", "portal", "start", "home", "hera", "registrant"],
    related: [
      "account-setup",
      "dashboard",
      "registrations",
      "parties",
      "user-management",
      "payments",
      "service-transfer",
    ],
    sections: [
      {
        heading: "What this portal is for",
        blocks: [
          {
            type: "p",
            text: "The registrant portal is where your organization registers properties with Hera, pays fees, tracks invoices and inspections, and manages who on your team can do that work.",
          },
          {
            type: "p",
            text: "Registrations, payments, and invoices belong to the organization, not to a single login. Anyone invited into the organization can usually see the same properties.",
          },
        ],
      },
      {
        heading: "First-time account setup",
        blocks: [
          {
            type: "p",
            text: "New users sign in or create a login, finish Complete Your Account Setup, then Verify Your Email before the dashboard opens. If a company invited you, an Account Invitation appears after that. See Create your account for the full walkthrough.",
          },
        ],
      },
      {
        heading: "The main screens",
        blocks: [
          {
            type: "ul",
            items: [
              "Dashboard — list or map of registrations (including Compliance Issues and Needs Attention), plus tabs for invoices, payments, inspections, unregistered properties, and (for admins) users.",
              "Register — start a new registration for a property and license type.",
              "Account — your profile, password reset, and (for the primary) invoice schedule.",
              "Users — people in the organization. Only admins see this tab.",
              "Help — this help center. Search or browse topics. Many screens also have a Help button that opens the matching article without leaving the page.",
            ],
          },
        ],
      },
      {
        heading: "Who can do what",
        blocks: [
          {
            type: "p",
            text: "Every active user can register and renew properties, use Pay Later, and view payments and invoices. Organization flags on the Users tab control the rest:",
          },
          {
            type: "ul",
            items: [
              "Admin — manage people on the Users tab. The primary is always an admin.",
              "Accounts Contact — who receives invoice and billing emails. Multiple people can be contacts. This is not the Stripe payment account.",
              "Billing — the single company Stripe customer used for card/ACH and the Customer Portal. Defaults to the primary. Change it with Set as Billing Account. Saved payment methods stay on that member’s Stripe customer.",
              "Can pay — Pay Now (card, ACH, check, invoice), cancel an awaiting check or invoice, and open Stripe billing. Admins can turn this off for themselves if they do not want to see Pay Now.",
            ],
          },
        ],
      },
      {
        heading: "Finding help",
        blocks: [
          {
            type: "p",
            text: "Use Help in the header or your account menu to open this help center. On a specific screen, the Help button opens that topic in a side panel so you can keep working. From the panel you can open the full article here, including related topics and search.",
          },
        ],
      },
    ],
  },
  {
    id: "account-setup",
    title: "Create your account",
    summary:
      "Sign in or sign up, complete your profile, verify email, and join a company invitation.",
    category: "getting-started",
    keywords: [
      "signup",
      "sign up",
      "create account",
      "login",
      "sign in",
      "verify email",
      "verification",
      "complete profile",
      "account setup",
      "individual",
      "company",
      "invite",
      "invitation",
      "password",
      "forgot password",
    ],
    related: ["getting-started", "account", "user-management", "dashboard"],
    sections: [
      {
        heading: "What you need",
        blocks: [
          {
            type: "p",
            text: "Every registrant uses their own email and password. Do not share a login. If you work for a company, the primary owner creates the company account first, then invites each person by email so the team shares one dashboard.",
          },
          {
            type: "p",
            text: "You can open this help topic during setup. After you finish, Help is also in the header and in your account menu.",
          },
        ],
      },
      {
        heading: "Sign in or create a login",
        blocks: [
          {
            type: "p",
            text: "Open the registrant portal. If you are not signed in, the sign-in page appears.",
          },
          {
            type: "ul",
            items: [
              "Existing users — enter the email and password you already use for this portal.",
              "New users — use the sign-up option on that page. Enter the email you will use for Hera and create a password that meets the rules shown there.",
              "Forgot password — use the reset option on the sign-in page. After you set a new password, sign in with it.",
              "Invited users — open the link in the invite email and create your login with that same email address. The email says you have been invited to join a Hera registrant account.",
            ],
          },
          {
            type: "p",
            text: "If a jurisdiction already has registrations under your email, signing in with that email connects you to that existing account. You still complete any missing profile fields and verify the email.",
          },
        ],
      },
      {
        heading: "Complete Your Account Setup",
        blocks: [
          {
            type: "p",
            text: "The first time you sign in, the portal opens Complete Your Account Setup. The subtitle is Please provide your details to continue. You cannot use the dashboard until this form is saved.",
          },
          {
            type: "p",
            text: "Required fields for a primary account:",
          },
          {
            type: "ul",
            items: [
              "Name",
              "Email — a valid email address",
              "Phone — a US number, shown as (XXX) XXX-XXXX",
              "Address Line 1, City, State (two letters), and ZIP Code (XXXXX or XXXXX-XXXX). Address Line 2 is optional. Street address suggestions can fill city, state, and ZIP.",
              "Company Name — optional. Use it when you want the organization name shown on registrations and invoices.",
            ],
          },
          {
            type: "p",
            text: "If you already belong to an organization as a secondary user, you only need name, email, and phone. The company’s address and company name appear as Primary address and Primary company name and cannot be changed here.",
          },
          {
            type: "p",
            text: "Click Complete Setup. A success message says Account information updated successfully, then the portal asks you to verify email.",
          },
        ],
      },
      {
        heading: "Verify Your Email",
        blocks: [
          {
            type: "p",
            text: "Verify Your Email asks you to check the inbox for the address shown on the page. Open the verification link in that message. If the email is missing, check spam. If you still do not have it, use Resend Verification Email. After you resend, the button waits five minutes (Resend in M:SS) before you can send another.",
          },
          {
            type: "p",
            text: "The portal checks verification automatically. When it succeeds, you go to the dashboard. You cannot use registrations, payments, or users until the email is verified.",
          },
        ],
      },
      {
        heading: "Joining a company (Account Invitation)",
        blocks: [
          {
            type: "p",
            text: "If an admin used Add User with your email, the next time you sign in you see Account Invitation: the primary contact’s name has requested that you join the company name, or their account if it is an individual organization.",
          },
          {
            type: "ul",
            items: [
              "Accept opens Confirm Account Merge: Your account will be merged with the company or primary name. Choose Yes to join. You become a Secondary user. New invites start without Admin and without Can pay. A success message says You have successfully joined the account. You then see the organization’s dashboard and registrations.",
              "Reject declines the invite and keeps your own account. A success message says Invite rejected. You can keep registering on your own.",
            ],
          },
          {
            type: "p",
            text: "Accepting links your login to the organization. Registrations, payments, and invoices belong to that organization, not to a single login.",
          },
        ],
      },
      {
        heading: "After setup",
        blocks: [
          {
            type: "p",
            text: "If the organization has no registrations yet, the dashboard shows Welcome to Your Dashboard and buttons to Register each available license type. If registrations already exist, you see the list and map.",
          },
          {
            type: "p",
            text: "Use Account in the account menu later to change profile details or reset your password. Admins use the Users tab to invite more people.",
          },
        ],
      },
      {
        heading: "Inspection-only logins",
        blocks: [
          {
            type: "p",
            text: "Some people are invited only to complete inspections. An admin can turn Registration Portal off (and Inspection Portal on) on the Users tab before that person ever signs in; the choice applies on first login. That login uses the same sign-in page, but the registrant portal sends you to the inspection portal instead of this dashboard. Ask an admin to turn on Registration Portal if you also need this portal.",
          },
        ],
      },
      {
        heading: "If you cannot get in",
        blocks: [
          {
            type: "ul",
            items: [
              "Use the same email that received the invite or that the jurisdiction already has on file.",
              "Reset the password from the sign-in page, or after you are in, from Account → Go to password reset page. Sign in again with the new password.",
              "If verification never arrives, wait for Resend Verification Email to become available, check spam, and confirm the address on the Verify Your Email page.",
              "If the portal says your account was not found after you were disabled, ask an admin to Re-enable you on the Users tab.",
              "If you still cannot sign in, contact the person who invited you or your jurisdiction’s Hera contact.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    summary:
      "Tabs, filters, map versus list, Compliance Issues, Needs Attention, Bulk Payment, and Register.",
    category: "getting-started",
    keywords: [
      "dashboard",
      "tabs",
      "filters",
      "map",
      "list",
      "balance due",
      "columns",
    ],
    related: [
      "registrations",
      "needs-attention",
      "compliance-issues",
      "payments",
      "inspections",
      "user-management",
      "service-transfer",
    ],
    sections: [
      {
        heading: "What you see",
        blocks: [
          {
            type: "p",
            text: "The dashboard is the home for your organization’s registrations. The title uses your company or account name. Tiles at the top can show compliance issues, properties that need attention, properties scheduled for Pay Later, and a Register button.",
          },
        ],
      },
      {
        heading: "Tabs",
        blocks: [
          {
            type: "ul",
            items: [
              "All Active Registrations — current registrations that are not in the inactive list.",
              "Compliance Issues — active registrations with an open compliance issue, including Ready for Check. These properties are not also listed under Needs Attention.",
              "Needs Attention — active registrations that are 30 to 90 days past renewal, or 30 or more days past renewal when that license type’s historical data has been cleaned (OMT not exempt). Open compliance issues are listed under Compliance Issues instead. See Needs Attention for details.",
              "Balance Due — registrations whose status is Balance Due (payment required, not awaiting check or invoice).",
              "Renewable — registrations that can be renewed now (typically expired or within 30 days of the renewal date).",
              "Inactive Registrations — cancelled, de-registered, or otherwise inactive records.",
              "Unregistered Properties — properties the jurisdiction has on file that are not yet registered to your organization.",
              "Invoices — invoices issued to your organization.",
              "Payments — check, card, ACH, and invoice payment history.",
              "Inspections — properties that need inspection assignment or a perform-inspection action.",
              "Users — organization people and roles. Only admins see this tab.",
            ],
          },
        ],
      },
      {
        heading: "Filters, columns, and map",
        blocks: [
          {
            type: "p",
            text: "Use jurisdiction, license type, and (when your organization has more than one user) registrant filters to narrow the list. Column visibility and column filters apply to the registration tables. Switch between list and map when the selected tab shows registrations.",
          },
          {
            type: "p",
            text: "Click a registration row to open details. Row actions such as Pay Now, Renew, De-register, Cancel, and Perform Inspection appear when that action is available for the record and for your permissions.",
          },
        ],
      },
      {
        heading: "Compliance Issues, Needs Attention, and Bulk Payment tiles",
        blocks: [
          {
            type: "ul",
            items: [
              "Compliance Issues — a red tile for properties with an open compliance issue, including Ready for Check. Use View properties to open the Compliance Issues tab. These properties are left off the Needs Attention tile.",
              "Needs Attention — late renewals (30 to 90 days, or 30 or more days when that license type’s historical data has been cleaned). Use View properties to open the Needs Attention tab. A scheduled Needs Attention email (Monday and Wednesday) may still mention late renewals and compliance issues when configured. See Needs Attention.",
              "Bulk Payment — properties already scheduled to be paid later. View properties to filter the list, or Pay now to pay several at once.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "registrations",
    title: "Registrations",
    summary:
      "Statuses, list actions, and how active, needs-attention, renewable, and inactive records differ.",
    category: "registrations",
    keywords: [
      "license",
      "status",
      "compliant",
      "balance due",
      "pay later",
      "renewable",
      "needs attention",
      "inactive",
    ],
    related: [
      "register-a-property",
      "renewals",
      "registration-details",
      "needs-attention",
      "compliance-issues",
      "payments",
      "service-transfer",
    ],
    sections: [
      {
        heading: "What a registration is",
        blocks: [
          {
            type: "p",
            text: "A registration (sometimes labeled as a license) is one property under one license type in a jurisdiction — for example a vacancy registration. It has a status, fees, questions, and optional inspections.",
          },
        ],
      },
      {
        heading: "Common statuses",
        blocks: [
          {
            type: "ul",
            items: [
              "Balance Due / Requires Payment — fees are owed. You can Pay Later (everyone) or Pay Now (if you can make payments).",
              "Pay Later — you chose to pay later; the registration stays on the dashboard until it is paid or cancelled.",
              "Payment Failed or Payment Processing — a card or ACH attempt did not finish. Pay Now can be tried again when payment is available.",
              "Awaiting check or invoice — you started a check or invoice payment; the registration waits until the jurisdiction records it or you cancel that payment.",
              "Compliant until renewal — approved and current. Renew appears when it is in the renewal window.",
              "Rejected — the jurisdiction did not approve it. Use Reapply to update answers and submit again.",
              "Cancelled or de-registered — the registration is inactive. It appears on the Inactive tab. If money is still owed, the list shows Balance Due instead of a settled cancelled status.",
            ],
          },
        ],
      },
      {
        heading: "List actions",
        blocks: [
          {
            type: "p",
            text: "Actions on a row depend on status and your permissions:",
          },
          {
            type: "ul",
            items: [
              "Pay Later — open the payment screen without collecting money. Everyone sees this on a payable registration if they do not have Can pay.",
              "Pay Now — open the payment screen to collect money. Shown instead of Pay Later when you have Can pay.",
              "Renew or Reapply — continue or resubmit the registration.",
              "De-register — end a compliant registration, sometimes with a fee and extra questions.",
              "Cancel — withdraw a registration that still has a balance due or a failed payment, before it is an approved ongoing registration.",
              "Perform Inspection — opens the inspection portal when an inspection is pending.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "register-a-property",
    title: "Register a property",
    summary:
      "How new registration works: address, questions, fees, Pay Later, and Pay Now.",
    category: "registrations",
    keywords: [
      "register",
      "new license",
      "address",
      "APN",
      "questions",
      "fees",
      "pay later",
    ],
    related: ["payments", "renewals", "registrations", "service-transfer"],
    sections: [
      {
        heading: "Start from the dashboard",
        blocks: [
          {
            type: "p",
            text: "Use Register on the dashboard. If the jurisdiction offers more than one license type, you pick the type first (for example vacancy). The Register button is available to every active user in the organization.",
          },
        ],
      },
      {
        heading: "The steps",
        blocks: [
          {
            type: "ol",
            items: [
              "Property address — enter the address when the license type requires a property.",
              "Address lookup — Hera verifies the address, jurisdiction, and parcel (APN) when that data is available.",
              "Questions — answer registration questions for that license type, including dates the ordinance requires.",
              "Periods and fees — review registration and late fees, optional inspection fees, and choose how to pay.",
            ],
          },
        ],
      },
      {
        heading: "Paying at the end",
        blocks: [
          {
            type: "ul",
            items: [
              "Pay Later — submit the registration without collecting money now. It stays on the dashboard with a balance due until someone pays or the registration is cancelled.",
              "Pay Now — card, ACH, check, or invoice (when the jurisdiction offers invoice pay). Only users with pay-now permission see these options.",
            ],
          },
          {
            type: "p",
            text: "Card and ACH add a processing fee. ACH requires you to sign in to your bank (instant verification). There is no microdeposit fallback. Check can add a check processing fee. Invoice pay typically has no extra payment fee. If you change fee amounts, leave a note for the review team.",
          },
        ],
      },
      {
        heading: "If this property is already registered",
        blocks: [
          {
            type: "p",
            text: "After address lookup, Hera shows a separate Existing Registration Check step before the registration questions when it finds a likely active registration at that jurisdiction and property — including one that is Balance Due. Review the existing registrant, OMT, AMS, case number (when set), effective date, status, address, and APN. Hera compares the APN, address, and location while keeping different apartment or unit numbers separate. If there is one match, choose Transfer; if there are several, select one and choose Transfer selected. You can also continue with a new registration. See Service transfer for the outstanding-payment choice and what moves.",
          },
        ],
      },
    ],
  },
  {
    id: "service-transfer",
    title: "Service transfer",
    summary:
      "Take over an existing registration at a property, including one that still has a balance due.",
    category: "registrations",
    keywords: [
      "transfer",
      "service transfer",
      "existing registration",
      "transfer existing",
      "balance due",
      "outstanding payments",
      "OMT",
      "take over",
    ],
    related: ["register-a-property", "registrations", "payments", "billing"],
    sections: [
      {
        heading: "When transfer appears",
        blocks: [
          {
            type: "p",
            text: "Start Register as usual. After Hera verifies the address and parcel, it shows a separate Existing Registration Check step before the registration questions if there is likely an active Hera registration at that jurisdiction and property. Review the existing registrant, OMT, AMS, case number (when set), effective date, status, address, and APN. Hera compares the APN, address, and location while keeping different apartment or unit numbers separate. That includes a registration whose status is Balance Due / Requires Payment, not only a current compliant one.",
          },
          {
            type: "p",
            text: "If there is one match, choose Transfer. If there are several, select a registration and choose Transfer selected. You can also continue with a new registration if you are not taking over an existing one.",
          },
        ],
      },
      {
        heading: "Outstanding payments",
        blocks: [
          {
            type: "p",
            text: "If the OMT party on the registration changes and the jurisdiction is set to ask, Outstanding payments appears. Yes — take responsibility for all outstanding payments moves prior unpaid periods to you. No — prior unpaid stay with the previous registrant. The current period always moves to the new registrant.",
          },
          {
            type: "p",
            text: "After a transfer, the dashboard Start Date column can show Service transfer: when that date is not the same as the start date. The registration details card can show Service Transfer Date.",
          },
        ],
      },
      {
        heading: "What does not happen here",
        blocks: [
          {
            type: "p",
            text: "This is not Make Primary (that transfers organization ownership on the Users tab). The OMT/AMS portal cannot start a service transfer. Paying, renewing, and cancelling after the transfer stay in this registrant portal.",
          },
        ],
      },
    ],
  },
  {
    id: "renewals",
    title: "Renewals and reapply",
    summary:
      "When Renew appears, what the renewal form asks, and how Reapply works after a rejection.",
    category: "registrations",
    keywords: ["renew", "reapply", "rejected", "expired", "renewal date"],
    related: ["registrations", "register-a-property", "payments"],
    sections: [
      {
        heading: "When you can renew",
        blocks: [
          {
            type: "p",
            text: "Renew is available when the registration is not in the middle of another process and is either expired or within 30 days of the renewal date. Those records also appear on the Renewable tab.",
          },
        ],
      },
      {
        heading: "What renewal asks",
        blocks: [
          {
            type: "p",
            text: "Renewal usually reuses the property already on file. You update registration questions as needed, then review fees for the new period. You can Pay Later or Pay Now the same way as a new registration.",
          },
        ],
      },
      {
        heading: "Reapply after rejection",
        blocks: [
          {
            type: "p",
            text: "If a registration is rejected, use Reapply. Update the information the jurisdiction needs and submit again. Fees and payment options follow the same rules as registration and renewal.",
          },
        ],
      },
    ],
  },
  {
    id: "registration-details",
    title: "Registration details",
    summary:
      "The details page and dashboard sheet: property, questions, payments, comments, and actions.",
    category: "registrations",
    keywords: [
      "details",
      "view",
      "questions",
      "comments",
      "property",
      "actions",
    ],
    related: [
      "registrations",
      "payments",
      "compliance-issues",
      "needs-attention",
      "parties",
      "cancel-and-deregister",
      "service-transfer",
    ],
    sections: [
      {
        heading: "Opening details",
        blocks: [
          {
            type: "p",
            text: "Click a registration on the dashboard to open details in a side sheet. View opens the full registration details page. Both show status, property, questions, payments, comments, and the actions that are currently allowed.",
          },
        ],
      },
      {
        heading: "What you can do there",
        blocks: [
          {
            type: "ul",
            items: [
              "Review answers and property information. After a service transfer, Service Transfer Date appears when it differs from the start date.",
              "See payment history for that registration. Download receipt covers the entire payment, including every property it paid for.",
              "Read Issues & Comments, including compliance issues. Report Issue Resolved when you have fixed an open compliance issue.",
              "Pay Now, Renew, Reapply, De-register, or Cancel when those actions apply. To take over another organization’s registration, use Register — not a Transfer button on this page.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "needs-attention",
    title: "Needs Attention",
    summary:
      "Which of your registrations appear under Needs Attention, including when historical data has been cleaned, and which emails you may receive.",
    category: "registrations",
    keywords: [
      "needs attention",
      "late renewal",
      "compliance",
      "email",
      "30",
      "90",
      "exempt",
      "historical data cleaned",
    ],
    related: [
      "compliance-issues",
      "dashboard",
      "registrations",
      "renewals",
      "party-exempt",
    ],
    sections: [
      {
        heading: "What is included",
        blocks: [
          {
            type: "p",
            text: "A registration appears on Needs Attention when its renewal is late and it does not have a compliance issue:",
          },
          {
            type: "ul",
            items: [
              "Late renewal — the renewal date is 30 to 90 days in the past, or 30 or more days in the past when historical data for that jurisdiction and license type has been cleaned. The OMT on the registration is not marked exempt, and the status is still eligible (cancelled, rejected, exempt, paid pending review, and de-registration-in-progress statuses are excluded from the late-renewal rule).",
              "Compliance issues are listed on the red Compliance Issues tile and tab instead, including when the same property is also late for renewal. The property stays there until the issue is cleared.",
            ],
          },
          {
            type: "p",
            text: "Renewals fewer than 30 days late are not in the late-renewal bucket by themselves. Renewals more than 90 days late stay off that bucket unless historical data for that license type has been cleaned. Balance Due and Renewable are separate tabs. When the OMT party is marked Exempt, that late renewal stays off Needs Attention and registration and late fees due are zero. You still register and renew. See Exempt.",
          },
        ],
      },
      {
        heading: "Emails",
        blocks: [
          {
            type: "ul",
            items: [
              "When a compliance issue is flagged, your organization’s primary account receives an email immediately. The subject names the property address when available, and photos attached to the issue can appear in the message.",
              "A scheduled Needs Attention email (Monday and Wednesday) may list late renewals in that window and properties with an active compliance issue. Licenses older than 90 days are included only when historical data for that license type has been cleaned. Whether that mail goes to the registrant primary depends on Hera configuration.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "compliance-issues",
    title: "Compliance issues",
    summary:
      "How compliance issues are flagged, emailed, tracked, and cleared. Registration status does not change.",
    category: "registrations",
    keywords: [
      "compliance",
      "issue",
      "ready for check",
      "report resolved",
      "email",
      "comments",
    ],
    related: [
      "needs-attention",
      "registration-details",
      "registrations",
      "dashboard",
    ],
    sections: [
      {
        heading: "What a compliance issue is",
        blocks: [
          {
            type: "p",
            text: "A compliance issue is a problem on a property that government or Hera Registry staff want your organization to see and fix. It is separate from an ordinary comment. Flagging an issue does not change the registration status.",
          },
          {
            type: "p",
            text: "Your organization’s primary account is emailed as soon as an issue is raised. The registration appears on the red Compliance Issues tile until the issue is cleared. It is not also listed on Needs Attention.",
          },
        ],
      },
      {
        heading: "What you should do",
        blocks: [
          {
            type: "ol",
            items: [
              "Open the registration from Compliance Issues or the full list.",
              "In Issues & Comments, read the Compliance Issue note and any photos.",
              "When you have fixed the problem, choose Report Issue Resolved…, describe what you did, attach photos if helpful, and save.",
            ],
          },
          {
            type: "p",
            text: "The label changes to Ready for Check. Do not use Notify Hera Registry for that step. The property stays on Compliance Issues until the party that raised the issue clears it. You can still Add Comment for ordinary notes while an issue is open.",
          },
        ],
      },
      {
        heading: "Clearing",
        blocks: [
          {
            type: "p",
            text: "Only the same party type that raised the issue can clear it (government clears government-raised issues; Hera Registry clears Hera-raised issues). After it is Cleared, the property drops off Compliance Issues.",
          },
        ],
      },
    ],
  },
  PARTIES_HELP_ARTICLE,
  PARTY_EXEMPT_HELP_ARTICLE,
  {
    id: "cancel-and-deregister",
    title: "Cancel and de-register",
    summary:
      "The difference between cancelling a registration that was never completed and de-registering an approved one.",
    category: "registrations",
    keywords: [
      "cancel",
      "de-register",
      "deregister",
      "withdraw",
      "end date",
      "pre-filing",
      "foreclosure case number",
    ],
    related: ["registrations", "registration-details", "payments"],
    sections: [
      {
        heading: "Cancel",
        blocks: [
          {
            type: "p",
            text: "Cancel withdraws a registration that still has a balance due or a failed payment — typically before it is an approved, ongoing registration. You explain the change in circumstances. Cancel is not available after the registration is rejected, already cancelled, or still processing a payment.",
          },
          {
            type: "p",
            text: "Cancelling clears any fees due. If you want to keep the balance due, set an effective end date on the registration instead of cancelling.",
          },
        ],
      },
      {
        heading: "De-register",
        blocks: [
          {
            type: "p",
            text: "De-register ends an approved registration (for example the property is no longer vacant). You enter an effective end date and answer any de-registration questions. Some jurisdictions charge a de-registration fee after you submit.",
          },
          {
            type: "p",
            text: "De-register is generally available when the registration was reviewed and is compliant, including when there is still a payable balance.",
          },
          {
            type: "p",
            text: "De-registering clears any fees due. If you want to keep the balance due, set an effective end date on the registration instead of de-registering.",
          },
        ],
      },
      {
        heading: "Pre-filing reason",
        blocks: [
          {
            type: "p",
            text: "Some jurisdictions approve a de-registration immediately when Reason for the De-Registration Request is Property no longer in default (pre-filing only). That happens after any de-registration fee is paid. A fee of zero is treated as no fee.",
          },
          {
            type: "p",
            text: "A foreclosure case number already on the registration keeps that request in review for staff, even when the reason is the pre-filing option. A blank case number, or a placeholder such as N/A, none, or unknown, still allows immediate approval where the jurisdiction has turned that option on.",
          },
        ],
      },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    summary:
      "Pay Now versus Pay Later, card, ACH, check, invoice, bulk pay, and cancelling an awaiting payment.",
    category: "payments",
    keywords: [
      "pay now",
      "pay later",
      "card",
      "ACH",
      "check",
      "stripe",
      "bulk",
      "awaiting",
      "receipt",
    ],
    related: ["invoices", "billing", "user-management", "register-a-property"],
    sections: [
      {
        heading: "Pay Later versus Pay Now",
        blocks: [
          {
            type: "p",
            text: "Pay Later records the registration or renewal without collecting money. Anyone in the organization can use it. On a payable registration, people without Can pay see a Pay Later action that opens that screen — the method picker is hidden, and Pay Later does not add a card or ACH processing fee. The property stays on the dashboard until it is paid, cancelled, or otherwise closed.",
          },
          {
            type: "p",
            text: "Pay Now collects fees immediately. Only admins and users granted Can pay see Pay Now, bulk Pay now, and the ability to cancel an awaiting check or invoice.",
          },
        ],
      },
      {
        heading: "Payment methods",
        blocks: [
          {
            type: "ul",
            items: [
              "Credit card — Stripe checkout, plus a card processing fee.",
              "ACH / US bank account — Stripe bank debit with instant bank login, plus an ACH fee (capped). There is no microdeposit fallback. If the bank cannot be verified that way, choose another method.",
              "Check — you receive a payment ID and mailing address. The registration stays awaiting check until the jurisdiction records the check or you cancel.",
              "Invoice — when the jurisdiction offers invoice pay. Similar to check: the invoice stays open until it is paid or cancelled.",
            ],
          },
        ],
      },
      {
        heading: "Paying several properties",
        blocks: [
          {
            type: "p",
            text: "The Balance Due tab, the Bulk Payment tile, and the Fee Payment screen let you pay more than one property at a time. You can include or exclude properties, adjust period amounts, then pay by card, ACH, check, or invoice — or create Pay Later records in bulk if you opened that flow from registrations that still need payment.",
          },
        ],
      },
      {
        heading: "Payments tab",
        blocks: [
          {
            type: "p",
            text: "The Payments tab lists check and card/ACH activity for the organization. Open a row to view details and download documents. If a check payment is still Awaiting Check and you can make payments, you can cancel it. Cancelling returns those registrations to an unpaid state so you can pay a different way.",
          },
        ],
      },
      {
        heading: "Download receipt",
        blocks: [
          {
            type: "p",
            text: "On a registration’s Payments card, Download receipt gives you the receipt for the entire payment, so a payment that covered more than one property shows every property on the one receipt. Receipts stay available after cancel, de-register, or a partial refund.",
          },
        ],
      },
      {
        heading: "If you leave Stripe checkout",
        blocks: [
          {
            type: "p",
            text: "If you cancel a card or ACH checkout, Hera abandons that Stripe session and restores Pay Later where appropriate. You can return to the dashboard or try Pay Now again.",
          },
        ],
      },
    ],
  },
  {
    id: "invoices",
    title: "Invoices",
    summary:
      "Jurisdiction invoices, paid versus unpaid, downloading, and cancelling an open invoice.",
    category: "payments",
    keywords: ["invoice", "unpaid", "paid", "download", "accounts contact"],
    related: ["payments", "billing", "account", "user-management"],
    sections: [
      {
        heading: "What appears on Invoices",
        blocks: [
          {
            type: "p",
            text: "The Invoices tab lists invoices your jurisdiction issued to the organization. Paid and Unpaid include open and settled invoice balances. Open a row to view details and download documents.",
          },
        ],
      },
      {
        heading: "Who receives invoice email",
        blocks: [
          {
            type: "p",
            text: "Accounts contact on the Users tab controls who receives invoice emails. That is separate from the Billing account (the Stripe customer) and from who can Pay Now.",
          },
        ],
      },
      {
        heading: "Cancelling an open invoice",
        blocks: [
          {
            type: "p",
            text: "If an invoice is still unpaid and pending, a user who can make payments can cancel it from the invoice details. The related registrations return to an unpaid state.",
          },
        ],
      },
    ],
  },
  {
    id: "billing",
    title: "Billing",
    summary:
      "Stripe Billing, Organization Billing, saved cards, and the one company billing account.",
    category: "payments",
    keywords: ["stripe", "billing", "customer", "portal", "card on file"],
    related: ["payments", "user-management"],
    sections: [
      {
        heading: "Billing in the account menu",
        blocks: [
          {
            type: "p",
            text: "If you can make payments, the account menu includes Billing (when you are the primary) or Organization Billing (when you are a secondary). That opens the Stripe customer portal to manage payment methods for the company’s billing account. Checkout and this portal always use the member marked Billing on the Users tab — even when someone else clicks Pay Now.",
          },
          {
            type: "p",
            text: "Users without pay-now permission do not see Billing. They can still Pay Later and view invoices and payments.",
          },
        ],
      },
      {
        heading: "Who Stripe bills",
        blocks: [
          {
            type: "p",
            text: "The organization has one Billing account. Stripe checkout, saved cards, and the billing portal use that member’s Stripe customer. It defaults to the primary. Organization admins change it with Set as Billing Account on the Users tab (or in that person’s details). Saved payment methods do not move when you switch. A checkout already in progress keeps the previous billing account until you cancel and start again. Switching billing does not change who can Pay Now or who receives invoice emails.",
          },
          {
            type: "p",
            text: "Make Primary can keep billing with the current billing person or bill the new primary instead. If the billing member leaves the organization (disabled), billing returns to the primary.",
          },
        ],
      },
    ],
  },
  {
    id: "user-management",
    title: "User management",
    summary:
      "Primary versus secondary, Roles (Admin, Accounts Contact, Can pay, Billing), portal access before first login, invites, and disable.",
    category: "organization",
    keywords: [
      "users",
      "admin",
      "invite",
      "invitation",
      "primary",
      "secondary",
      "can pay",
      "roles",
      "make primary",
      "disable",
      "accounts contact",
      "billing account",
      "set as billing account",
      "portals",
      "inspection-only",
    ],
    related: [
      "account-setup",
      "billing",
      "account",
      "payments",
      "getting-started",
    ],
    sections: [
      {
        heading: "Overview",
        blocks: [
          {
            type: "p",
            text: "User management controls who belongs to this organization, who can invite and edit people, and who can collect money. Every organization has one primary owner and any number of invited secondary users. There is no separate Individual or Company account type — company name on the primary is optional display. Registrations, payments, and invoices belong to the organization, not to a single login.",
          },
        ],
      },
      {
        heading: "What every user can do",
        blocks: [
          {
            type: "p",
            text: "Anyone in the organization who is not disabled can:",
          },
          {
            type: "ul",
            items: [
              "Register and renew properties",
              "Use Pay Later",
              "See payment and invoice lists, statuses, and documents",
              "Open registration details and perform inspections they are allowed to do",
            ],
          },
          {
            type: "p",
            text: "The Roles column adds Admin (manage people) and Can pay (Pay Now and Stripe billing). Accounts Contact and Billing are separate organization flags, not extra logins.",
          },
        ],
      },
      {
        heading: "Primary account",
        blocks: [
          {
            type: "p",
            text: "The primary account is the owner of the organization. It is always an admin, and that admin status cannot be turned off. Billing defaults to the primary until an admin uses Set as Billing Account on someone else. You cannot Disable the primary from this portal.",
          },
          {
            type: "p",
            text: "An admin can transfer ownership with Make Primary. The new primary becomes admin. The previous primary stays an admin as a secondary user unless another admin later turns Admin off. Make Primary asks whether Stripe billing stays with the current billing person or moves to the new primary. Saved cards do not move.",
          },
        ],
      },
      {
        heading: "Admins",
        blocks: [
          {
            type: "p",
            text: "Only admins can open the Users tab. Non-admins cannot see or change the organization user list.",
          },
          {
            type: "p",
            text: "Admins can:",
          },
          {
            type: "ul",
            items: [
              "Invite people with Add User",
              "Edit a secondary user’s name, email, phone, and company name",
              "Cancel a pending invite",
              "Disable or re-enable a secondary user",
              "Toggle Admin, Accounts Contact, and Can pay on the Roles column (including on a pending invite)",
              "Choose the company billing account with Set as Billing Account",
              "Set Registration Portal and Inspection Portal access, including before first login",
              "Transfer primary ownership with Make Primary",
            ],
          },
          {
            type: "ul",
            items: [
              "The primary user is always an admin. That cannot be revoked.",
              "Other users can be made admin only by an existing admin.",
            ],
          },
        ],
      },
      {
        heading: "Who can make payments",
        blocks: [
          {
            type: "p",
            text: "Can pay is separate from Admin. Admins can grant or revoke it for any org user, including themselves, so they can hide Pay Now when they do not want payment buttons. Non-admins can be given Can pay so they can Pay Now without becoming admins.",
          },
          {
            type: "p",
            text: "If a user can pay, they can Pay Now with card, ACH, check, or invoice (including bulk Pay Now); cancel an awaiting check or invoice payment; and open Stripe Billing or Organization Billing.",
          },
          {
            type: "p",
            text: "If a user cannot pay, they can still register, renew, use Pay Later (including the Pay Later row action on a payable registration), and view payment history. They will not see Pay Now or Stripe billing.",
          },
          {
            type: "p",
            text: "New invites start without admin and without pay-now. You can turn those roles on while the row is still Pending. If you disable the only person who can pay, the primary is given payment permission so the organization is never left unable to Pay Now.",
          },
        ],
      },
      {
        heading: "Inviting and managing users",
        blocks: [
          {
            type: "p",
            text: "On the Users tab, Add User asks for Email Address. After you add them, the page says When that email next logs in, they will be asked to confirm they want to join your account.",
          },
          {
            type: "ul",
            items: [
              "If they do not have a Hera login yet, they receive an email: You have been invited to join a Hera registrant account. They create a login with that same email, complete profile setup, and verify email. Then Account Invitation appears.",
              "If they already have a login, Account Invitation appears the next time they sign in.",
              "They Accept (then confirm Confirm Account Merge) or Reject. See Create your account for the invitee steps.",
              "Pending rows stay pending until they join. Name and phone stay hidden. Cancel invite is the only row action until they join.",
              "Click a row — including the primary — to view details. Make Primary and Disable apply to secondaries only. Set as Billing Account appears whenever that person is not already billing.",
              "Disable stops a secondary’s login but keeps their registrations on the organization. Re-enable restores access. A disabled person who tries to sign in cannot open the portal until someone re-enables them. If they were the billing member, billing returns to the primary.",
            ],
          },
        ],
      },
      {
        heading: "Before they sign in",
        blocks: [
          {
            type: "p",
            text: "You do not have to wait for a pending invite to accept before you decide what they can do. On that row you can toggle Admin, Accounts Contact, and Can pay, and set Portals. Portal switches show Applies when this user first logs in. Billing cannot be assigned until they have joined.",
          },
        ],
      },
      {
        heading: "Accounts contact",
        blocks: [
          {
            type: "p",
            text: "Accounts Contact on the Roles column controls who receives invoice and billing emails. Multiple people can be contacts. It is not the Stripe payment account. You can turn it on for a pending invite before that person has signed in.",
          },
        ],
      },
      {
        heading: "Billing account",
        blocks: [
          {
            type: "p",
            text: "Billing is the single company Stripe customer used for card and ACH checkout and the Customer Portal. There is exactly one per organization. It defaults to the primary. Set as Billing Account on another member unsets everyone else and asks you to confirm. Saved cards and bank accounts stay on that member’s Stripe customer and do not move when you switch. Switching back reuses that member’s existing Stripe customer. A checkout already in progress keeps the previous billing account until you cancel and start it again.",
          },
        ],
      },
      {
        heading: "Portals",
        blocks: [
          {
            type: "p",
            text: "The Portals column controls whether a person’s login can open this registrant portal, the inspection portal, or both.",
          },
          {
            type: "ul",
            items: [
              "Registration — this portal. The primary login cannot have Registration turned off.",
              "Inspection — the inspection portal for assigned properties. The primary always has inspection access to all organization properties.",
              "If Registration is off, that login is inspection-only. Signing in to the registrant portal sends them to the inspection portal.",
              "For a pending invite with no login yet, the same switches stage access. The note reads Applies when this user first logs in.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "account",
    title: "Account settings",
    summary:
      "Profile fields, password reset, email changes, and the invoice weekday schedule.",
    category: "organization",
    keywords: [
      "account",
      "profile",
      "password",
      "email",
      "phone",
      "invoice schedule",
      "weekday",
      "individual",
      "company",
      "secondary",
      "reset password",
    ],
    related: [
      "account-setup",
      "user-management",
      "invoices",
      "getting-started",
    ],
    sections: [
      {
        heading: "Your profile",
        blocks: [
          {
            type: "p",
            text: "Account in the user menu opens Account Management (Update your account information and preferences). The same fields you entered at Complete Your Account Setup can be updated here, then saved with Save Changes.",
          },
        ],
      },
      {
        heading: "Primary account",
        blocks: [
          {
            type: "p",
            text: "If you are the primary, you can edit name, optional company name, email, phone, and address. Company name is optional and is used for display on registrations and invoices. The company’s Billing account is shown on this page for context; organization admins change it on the Users tab.",
          },
          {
            type: "ul",
            items: [
              "Name or Contact Name, Email, and Phone (US format).",
              "Address Line 1, optional Address Line 2, City, State (two letters), and ZIP Code. Address Line 1 can suggest a US street address and fill the other address fields.",
            ],
          },
          {
            type: "p",
            text: "Changing your email updates the address you use to sign in. You may need to verify the new address before you can use the rest of the portal. Changing your name also updates the name shown on comments you write.",
          },
        ],
      },
      {
        heading: "Secondary users",
        blocks: [
          {
            type: "p",
            text: "If you are a secondary user, the page says This account is linked to a primary account. You can still edit your own name, email, and phone. Primary company name and Primary address are shown read-only. Organization address and the invoice weekday schedule are managed by the primary.",
          },
        ],
      },
      {
        heading: "Password",
        blocks: [
          {
            type: "p",
            text: "Reset Password explains: If you need to change your password, click the button below. This will take you to a password reset page. After your password is reset, you will need to login into the application again with your new password.",
          },
          {
            type: "p",
            text: "Go to password reset page sends you to that reset page. After it succeeds, sign in again with the new password. You can also start a reset from the sign-in page if you are not already in the portal.",
          },
        ],
      },
      {
        heading: "Invoice schedule",
        blocks: [
          {
            type: "p",
            text: "Automated invoice weekdays (UTC) lets the primary choose Mon–Sun when scheduled batch invoices may run. Times are UTC (for example, Monday 03:00 UTC is Sunday evening in US Pacific). Invoice emails go to the accounts contact set under Dashboard → Users.",
          },
          {
            type: "p",
            text: "Secondary users can see the schedule. The page says This schedule applies to your organization and is managed by the primary account holder. Only the primary can Save schedule or Clear selection.",
          },
        ],
      },
    ],
  },
  {
    id: "inspections",
    title: "Inspections",
    summary:
      "The Inspections tab, assigning inspectors, inviting inspection users, and Perform Inspection.",
    category: "inspections",
    keywords: [
      "inspection",
      "assign",
      "perform",
      "inspector",
      "dispatch",
      "pending",
    ],
    related: ["dashboard", "registrations", "user-management", "account-setup"],
    sections: [
      {
        heading: "Inspections tab",
        blocks: [
          {
            type: "p",
            text: "The Inspections tab lists properties that currently need inspection assignment. You can select properties, assign them to someone in the organization, or invite a person by email and assign them in one step. Use the map when properties have coordinates.",
          },
        ],
      },
      {
        heading: "Assigning inspections",
        blocks: [
          {
            type: "p",
            text: "Select one or more properties, then assign them to someone already in the organization, or invite a person by email and assign them in the same step. Use the map when properties have coordinates.",
          },
        ],
      },
      {
        heading: "Perform Inspection",
        blocks: [
          {
            type: "p",
            text: "When a registration’s newest inspection is pending, the property details show Inspection Pending and Perform Inspection. Self-inspections are done on a mobile device at the property: scan the QR code on the Actions tile, sign in if asked, answer the questions, add photos, and submit. The registration list also has Perform Inspection, which opens the same inspection portal.",
          },
        ],
      },
    ],
  },
  {
    id: "unregistered-properties",
    title: "Unregistered properties",
    summary:
      "Properties the jurisdiction has identified that are not yet registered to your organization.",
    category: "inspections",
    keywords: ["unregistered", "foreclosure", "not registered", "jurisdiction"],
    related: ["register-a-property", "dashboard", "registrations"],
    sections: [
      {
        heading: "What this tab is",
        blocks: [
          {
            type: "p",
            text: "Unregistered Properties shows properties linked to your organization that are not yet a Hera registration — often identified by the jurisdiction (for example from foreclosure or ordinance work). Filters on the dashboard still apply.",
          },
          {
            type: "p",
            text: "Use this list to see addresses the jurisdiction expects you to register. Start a registration from the dashboard Register flow when you are ready.",
          },
        ],
      },
    ],
  },
];

const ARTICLES_BY_ID = new Map(
  HELP_ARTICLES.map((article) => [article.id, article]),
);

export function getHelpArticle(id: string): HelpArticle | undefined {
  return ARTICLES_BY_ID.get(id as HelpArticleId);
}

export function isHelpArticleId(id: string): id is HelpArticleId {
  return ARTICLES_BY_ID.has(id as HelpArticleId);
}

export function articlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return HELP_ARTICLES.filter((article) => article.category === categoryId);
}
